/** Turning an editor draft into something the kernel will build a game from,
 *  and back again.
 *
 * This is the half the shell cannot do. A `NewGameSetup` carries an
 * `AdventureMapState`, whose `moveCost` and `occupant` planes are *derived* -
 * `setTerrain` recomputes a cell's cost from its terrain, its road and whatever
 * object blocks it, and `newGame` lays every object's footprint into
 * `occupant`. Both live in `core/src/map`, which is a rule module the shell may
 * not import (I10). So the draft holds only what a person authors and the
 * conversion happens here, on save, on validate and on play.
 *
 * The reverse direction matters just as much: an author must be able to open a
 * map file, or a generated board, and keep editing it. `draftFromSetup` is what
 * makes "generate a board, then fix it by hand" a workflow rather than a
 * one-way door.
 */

import type {
  AdventureMapState, ContentRegistry, FactionId, HeroDefId, HeroSetup, NewGameSetup,
  ObjectSetup, PlayerId, PlayerSetup, ScenarioFile, ScenarioMeta, TerrainKind,
} from "@game/core";
import { TERRAINS, createMap, makeScenario, setTerrain } from "@game/core";
import type { DraftObject, DraftPlayer, EditorDraft } from "@game/ui";
import { blankDraft } from "@game/ui";

/**
 * The draft's terrain plane, laid into a real map.
 *
 * Cell by cell through `setTerrain` rather than by copying the array, because
 * copying would leave `moveCost` at whatever the fill was: a map painted with
 * swamp and rock would cost the same to cross as one painted with grass, and
 * nothing downstream would notice until a hero walked over a mountain.
 */
export function compileMap(d: EditorDraft): AdventureMapState {
  const map = createMap(d.width, d.height, d.levels, { terrain: "grass" });
  for (let z = 0; z < d.levels; z++) {
    for (let y = 0; y < d.height; y++) {
      for (let x = 0; x < d.width; x++) {
        const i = (z * d.height + y) * d.width + x;
        const kind = TERRAINS[d.terrain[i] ?? 0] ?? "grass";
        setTerrain(map, x, y, z, kind);
      }
    }
  }
  return map;
}

const objectSetup = (o: DraftObject): ObjectSetup => ({
  kind: o.kind as ObjectSetup["kind"],
  def: o.def,
  x: o.x, y: o.y, z: o.z,
  // Always present rather than conditionally spread: `exactOptionalPropertyTypes`
  // distinguishes an absent key from an explicit `null`, and `owner: null` is
  // exactly what an unflagged object means.
  owner: o.owner === null ? null : (o.owner as PlayerId),
  // The author's overrides, on top of whatever the def says. `newGame` merges
  // them the same way the kernel reads them at runtime, so a map that sets a
  // mine's yield gets that yield and a map that sets nothing gets the def's.
  payload: o.payload,
});

export interface CompileOptions {
  /**
   * Which hero definition a faction starts with; `startingHero` in
   * `newgame.ts`. A draft names a faction and a place, not a face. Generated
   * maps pass a seed so the face is a roster draw; a hand-authored draft
   * that calls this without a seed still gets the alphabetically-first one.
   */
  readonly heroFor: (faction: string) => HeroDefId;
}

function playerSetup(p: DraftPlayer, opts: CompileOptions): PlayerSetup {
  const faction = p.faction as FactionId;
  const heroes: HeroSetup[] = p.hero === null ? [] : [{
    def: opts.heroFor(p.faction), x: p.hero.x, y: p.hero.y, z: p.hero.z,
  }];
  return {
    id: p.id as PlayerId,
    kind: p.kind,
    colour: p.colour,
    faction,
    towns: p.town === null
      ? []
      : [{ faction, x: p.town.x, y: p.town.y, z: p.town.z }],
    heroes,
  };
}

export function compileSetup(
  d: EditorDraft, content: ContentRegistry, opts: CompileOptions,
): NewGameSetup {
  const players: PlayerSetup[] = d.players.map((p) => playerSetup(p, opts));

  return {
    rootSeed: d.seed,
    map: compileMap(d),
    players,
    objects: d.objects.map(objectSetup),
    victory: d.victory,
    contentVersion: content.contentVersion,
  };
}

/** The whole write path: a draft as a map file. */
export function compileScenario(
  d: EditorDraft, content: ContentRegistry, opts: CompileOptions,
): ScenarioFile {
  const meta: Partial<ScenarioMeta> = {
    name: d.meta.name,
    description: d.meta.description,
    author: d.meta.author,
  };
  return makeScenario(compileSetup(d, content, opts), meta, content.contentVersion);
}

// ------------------------------------------------------------------- back

/** A setup, as a draft an author can keep editing. */
export function draftFromSetup(
  setup: NewGameSetup, meta?: Partial<EditorDraft["meta"]>,
): EditorDraft {
  const m = setup.map;
  const base = blankDraft(m.width, m.height, m.levels);
  const terrain = new Uint8Array(m.terrain.length);
  terrain.set(m.terrain);

  const objects: DraftObject[] = (setup.objects ?? []).map((o, i) => ({
    key: `o${String(i + 1)}`,
    def: o.def,
    kind: o.kind as string,
    x: o.x, y: o.y, z: o.z,
    owner: (o.owner ?? null) as string | null,
    payload: o.payload ?? {},
  }));

  const players: DraftPlayer[] = setup.players.map((p) => ({
    id: p.id as string,
    kind: p.kind,
    colour: p.colour,
    faction: p.faction as string,
    town: p.towns?.[0] === undefined
      ? null
      : { x: p.towns[0].x, y: p.towns[0].y, z: p.towns[0].z },
    hero: p.heroes?.[0] === undefined
      ? null
      : { x: p.heroes[0].x, y: p.heroes[0].y, z: p.heroes[0].z },
  }));

  return {
    ...base,
    meta: { ...base.meta, ...meta },
    seed: setup.rootSeed,
    terrain,
    objects,
    players: players.length > 0 ? players : base.players,
    victory: setup.victory ?? "defeat_all",
  };
}

/** The terrains a capital may not stand on, off the registry rather than by name. */
export function waterTerrains(content: ContentRegistry): ReadonlySet<string> {
  const out = new Set<string>();
  for (const t of content.terrain.values()) {
    if (t.water || t.impassable) out.add(t.id as string);
  }
  return out;
}

export const scenarioFileName = (d: EditorDraft): string => {
  const slug = d.meta.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug === "" ? "map" : slug}.realm-map.json`;
};

export type { TerrainKind };
